/* eslint-disable linebreak-style */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import Axios from 'axios'
import AuthService from '../../auth/AuthService'
import { User } from 'oidc-client'
import { CurrentUserModel } from '../../domain/Auth/CurrentUser'

interface IAuthState {
  user?: User | null
  currentUser?: CurrentUserModel
}

const authService = new AuthService()

const initialState: IAuthState = {}

function tiposDeUsuario(profile: { [key: string]: any }): string[] {
  if (profile?.Vinculo === '0') return ['aluno']

  const role = profile?.role
  const tipos = (Array.isArray(role) ? role : role ? [role] : [])
    .filter((item: unknown): item is string => typeof item === 'string' && item.includes('Promais'))
    .map((item) => item.replace('Promais.', '').replace(/\..*/g, '').toLowerCase())

  if (profile?.['Promais.Comissao'] === '1' && !tipos.includes('comissao')) {
    tipos.push('comissao')
  }

  return tipos
}

const authSlice = createSlice({
  name: 'authSlice',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      const user = action.payload
      state.user = user
      if (user) {
        state.currentUser = {
          name: user.profile.Nome,
          cpf: user.profile.CPF,
          tipoUsuario: tiposDeUsuario(user.profile),
          categorias: Object.assign(
            {},
            ...Object.keys(user.profile)
              .filter((key) => {
                const valor = user.profile[key]
                return key.includes('Promais.Categoria') && typeof valor === 'string' && !valor.includes('Sem Permissao')
              })
              .sort((keyA, keyB) => keyA.localeCompare(keyB))
              .map((key) => ({
                [key.replace('Promais.Categoria.', '')]: user.profile[key],
              }))
          ),
        }
      }
    },
  },
})

const customActions = {
  signinRedirectCallbackAction: () => {
    return async (dispatch: any) => {
      let user = await authService.getUser()
      if (user === null || user === undefined) {
        user = await authService.signinRedirectCallback()
      }
      dispatch(AuthActions.setUser(user))
    }
  },
  tokenRenewAction: () => {
    return async (dispatch: any) => {
      if (process.env.REACT_APP_DEV_AUTH === 'true') {
        return
      }
      let result: User | null = await authService.getUser()
      if (result && result.expired) {
        await authService.renewToken()
        result = await authService.getUser()
      }
      dispatch(AuthActions.setUser(result))
    }
  },
  loginAction: () => {
    return async (dispatch: any) => {
      if (process.env.REACT_APP_DEV_AUTH === 'true') {
        const baseUrl = process.env.REACT_APP_API_URL || ''
        const result = await Axios.get(`${baseUrl}dev/me`)
        dispatch(AuthActions.setUser({
          profile: result.data,
          expired: false,
        } as User))
        return
      }
      let result: User | null = await authService.getUser()
      if (!result) {
        await authService.login()
        result = await authService.getUser()
      }
      dispatch(AuthActions.setUser(result))
    }
  },
  silentRenewAction: () => {
    return async (dispatch: any) => {
      if (process.env.REACT_APP_DEV_AUTH === 'true') {
        return
      }
      const result: User | undefined = await authService.signinSilentCallback()

      if (result) {
        dispatch(AuthActions.setUser(result))
      }
    }
  },
}

const AuthReducer = authSlice.reducer
const AuthActions = {
  ...authSlice.actions,
  ...customActions,
}

export { AuthReducer, AuthActions }
